import { useEffect, useState } from "react";
import api from "@/lib/api";
import { FoundingPartnersSection } from "@/pages/MarketingDashboard";
import { toast } from "sonner";
import { DollarSign, Plus, Trash2, RefreshCw, X } from "lucide-react";

const DIVISIONS = ["D1", "D2", "D3", "NAIA", "MCLA D1", "MCLA D2", "WCLA"];
const GENDERS = ["Men", "Women"];
const POSITIONS = ["Head Coach", "Associate HC / Coordinator", "Assistant Coach", "GA / Part-Time"];
const CONFIDENCE = [
  { v: "verified", label: "VERIFIED", cls: "border-green-600 text-green-400" },
  { v: "publicly_reported", label: "REPORTED", cls: "border-volt text-volt" },
  { v: "estimate", label: "CC ESTIMATE", cls: "border-neutral-500 text-neutral-300" },
  { v: "coach_submitted", label: "COACH-SUBMITTED", cls: "border-blaze text-blaze" },
];

const fmt = (n) => (n == null ? "—" : `$${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`);
const range = (lo, hi, note) =>
  lo == null && hi == null ? "undisclosed" : `${fmt(lo)}${hi != null && hi !== lo ? `–${fmt(hi)}` : hi == null ? "+" : ""}${note ? ` ${note}` : ""}`;

const EMPTY = { program_name: "", gender: "Men", division: "D1", position: "Assistant Coach", salary_min: "", salary_max: "", confidence: "verified", source: "", location: "", notes: "" };

export default function AdminCompensation() {
  const [records, setRecords] = useState(null);
  const [summary, setSummary] = useState(null);
  const [benchmarks, setBenchmarks] = useState([]);
  const [filters, setFilters] = useState({ division: "", gender: "", position: "", confidence: "", q: "" });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [indices, setIndices] = useState({});
  const [sortByIndex, setSortByIndex] = useState(false);
  const [coverage, setCoverage] = useState([]);
  const [sweep, setSweep] = useState([]);
  const [sweepBusy, setSweepBusy] = useState(false);

  const loadSweep = () => api.get("/admin/comp/sweep").then(({ data }) => setSweep(data)).catch(() => {});
  const loadCoverage = () => api.get("/admin/comp/coverage").then(({ data }) => setCoverage(data)).catch(() => {});

  const load = () => {
    const params = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    api.get(`/admin/comp/records${params ? `?${params}` : ""}`).then(({ data }) => setRecords(data)).catch(() => toast.error("Load failed"));
    api.get("/admin/comp/summary").then(({ data }) => setSummary(data)).catch(() => {});
  };

  useEffect(() => { load(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/admin/comp/benchmarks").then(({ data }) => setBenchmarks(data)).catch(() => {});
    api.get("/admin/comp/indices").then(({ data }) => setIndices(data)).catch(() => {});
    loadSweep(); loadCoverage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runSweep = async () => {
    setSweepBusy(true);
    try {
      const { data } = await api.post("/admin/comp/sweep/run");
      toast.success(`Sweep — queued ${data.queued} salary finds, skipped ${data.skipped}`);
      loadSweep();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sweep failed"); }
    finally { setSweepBusy(false); }
  };

  const handleSweep = async (id, action) => {
    try {
      await api.post(`/admin/comp/sweep/${id}/${action}`);
      toast.success(action === "approve" ? "Added as VERIFIED record." : "Dismissed.");
      loadSweep(); load(); loadCoverage();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, salary_min: form.salary_min ? parseInt(form.salary_min) : null, salary_max: form.salary_max ? parseInt(form.salary_max) : null };
      if (form.id) await api.patch(`/admin/comp/records/${form.id}`, payload);
      else await api.post("/admin/comp/records", payload);
      toast.success(form.id ? "Record updated." : "Record added.");
      setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/comp/records/${id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed"); }
  };

  const generate = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post("/admin/comp/generate-estimates");
      toast.success(`Estimates — created ${data.created}, skipped ${data.skipped}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setGenBusy(false); }
  };

  const badge = (c) => CONFIDENCE.find((x) => x.v === c) || CONFIDENCE[2];
  const mainBench = benchmarks.filter((b) => !b.division_label.includes("Tier"));
  const tierBench = benchmarks.filter((b) => b.division_label.includes("Tier"));
  const shownRecords = records && sortByIndex
    ? [...records].sort((a, b) => (indices[b.job_id] ?? -1) - (indices[a.job_id] ?? -1))
    : records;

  return (
    <div className="text-white">
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-7xl" data-testid="admin-compensation-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs tracking-widest text-blaze flex items-center gap-2"><DollarSign size={13} /> ADMIN ONLY · PROPRIETARY DATA</div>
            <h1 className="heading text-4xl md:text-5xl mt-2">COMPENSATION DATABASE</h1>
            <p className="text-neutral-400 mt-2 text-sm max-w-2xl">2026 Benchmark Edition — Men's &amp; Women's NCAA Lacrosse. Verified compensation is kept strictly separate from modeled market estimates.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={generate} disabled={genBusy} data-testid="generate-estimates-btn"
              className="inline-flex items-center gap-2 text-xs tracking-widest heading border border-volt text-volt px-3 py-2 hover:bg-volt hover:text-white transition-colors disabled:opacity-50">
              <RefreshCw size={13} /> {genBusy ? "MODELING…" : "GENERATE ESTIMATES FOR OPEN JOBS"}
            </button>
            <button onClick={() => setForm({ ...EMPTY })} data-testid="add-record-btn"
              className="inline-flex items-center gap-2 text-xs tracking-widest heading bg-blaze text-white px-3 py-2 hover:glow-blaze transition-all">
              <Plus size={13} /> ADD RECORD
            </button>
          </div>
        </div>

        {summary && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid="comp-summary-chips">
            <span className="px-3 py-1.5 border border-neutral-700 text-[10px] tracking-widest">TOTAL: <b className="text-white">{summary.total}</b></span>
            <span className="px-3 py-1.5 border border-green-600 text-[10px] tracking-widest text-green-400">VERIFIED: <b>{summary.verified}</b></span>
            <span className="px-3 py-1.5 border border-volt text-[10px] tracking-widest text-volt">REPORTED: <b>{summary.publicly_reported}</b></span>
            <span className="px-3 py-1.5 border border-neutral-500 text-[10px] tracking-widest text-neutral-300">ESTIMATES: <b>{summary.estimate}</b></span>
            <span className="px-3 py-1.5 border border-blaze text-[10px] tracking-widest text-blaze">COACH-SUBMITTED: <b>{summary.coach_submitted}</b></span>
            <span className="px-3 py-1.5 border border-neutral-700 text-[10px] tracking-widest">WITH SALARY: <b className="text-white">{summary.with_salary}</b></span>
          </div>
        )}

        {/* Benchmark ranges */}
        <div className="mt-8 border border-neutral-800 bg-appcard p-5" data-testid="benchmark-table">
          <div className="text-xs tracking-widest text-volt">2026 BENCHMARK RANGES · DIVISION × POSITION</div>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] tracking-widest text-neutral-500 border-b border-neutral-800">
                <th className="text-left py-2 pr-3">DIVISION</th><th className="text-left pr-3">POSITION</th>
                <th className="text-left pr-3">MEN'S MARKET</th><th className="text-left">WOMEN'S MARKET</th>
              </tr></thead>
              <tbody>
                {mainBench.map((b) => (
                  <tr key={`${b.division_label}-${b.position}`} className="border-b border-neutral-900">
                    <td className="py-2 pr-3 font-semibold">{b.division_label}</td>
                    <td className="pr-3 text-neutral-300">{b.position}</td>
                    <td className="pr-3">{range(b.men_min, b.men_max, b.note && b.division_label.includes("Elite") ? "" : b.note === "+ tuition" ? "+ tuition" : "")}</td>
                    <td>{range(b.women_min, b.women_max, b.note === "+ tuition" ? "+ tuition" : "")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {tierBench.length > 0 && (
            <div className="mt-5 grid md:grid-cols-2 gap-6">
              {["Men D1 Tier", "Women D1 Tier"].map((lbl) => (
                <div key={lbl}>
                  <div className="text-[10px] tracking-widest text-neutral-500 border-b border-neutral-800 pb-1.5">{lbl.toUpperCase().replace("TIER", "HEAD-COACH TIERS")}</div>
                  {tierBench.filter((b) => b.division_label === lbl).map((b) => (
                    <div key={b.position} className="flex justify-between text-xs py-1.5 border-b border-neutral-900">
                      <span className="text-neutral-300">{b.position}</span>
                      <span className="font-semibold">{range(b.men_min ?? b.women_min, b.men_max ?? b.women_max)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          <p className="text-[10px] text-neutral-500 mt-4">CoachConnect benchmark ranges — not claims that every coach within a category earns within the band. Division alone does not determine compensation.</p>
        </div>

        {/* Compensation Index methodology */}
        <div className="mt-8 border border-neutral-800 bg-appcard p-5" data-testid="comp-index-methodology">
          <div className="text-xs tracking-widest text-blaze">COACHCONNECT COMPENSATION INDEX · METHODOLOGY</div>
          <p className="text-sm text-neutral-300 mt-2 max-w-3xl">Every job is scored 0–100 across five weighted dimensions — because a $72K D3 head-coaching job with housing and camp income can beat a $90K D1 assistant job in an expensive market.</p>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
            {[["Guaranteed Compensation", 35, "Base salary vs the benchmark band for that division, position & gender"],
              ["Total Financial Value", 20, "Additional duties pay, healthcare/dental/vision, 401k match, grad tuition, housing, free field access"],
              ["Program / Career Value", 20, "Division & program trajectory — the career doors the job opens"],
              ["Cost of Living", 15, "State-level index — what the money is actually worth locally"],
              ["Family & Quality of Life", 10, "Benefits completeness, housing, 12-month stability"]].map(([label, w, desc]) => (
              <div key={label} className="border border-neutral-800 p-3">
                <div className="heading text-2xl" style={{ color: "#FF3B30" }}>{w}%</div>
                <div className="text-[10px] tracking-widest text-white mt-1">{String(label).toUpperCase()}</div>
                <div className="text-[10px] text-neutral-500 mt-1.5 leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-neutral-500 mt-3">Index inputs are collected per job posting in the admin job editor (Compensation Index Data section): coach salary, additional-duties salary, healthcare/dental/vision, retirement match, grad tuition, housing, dedicated field for camps/clinics, other perks.</p>
        </div>

        {/* Verified comp sweep queue */}
        <div className="mt-8 border border-neutral-800 bg-appcard p-5" data-testid="comp-sweep-panel">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs tracking-widest text-volt">VERIFIED COMP SWEEP · {sweep.length} PENDING</div>
            <button onClick={runSweep} disabled={sweepBusy} data-testid="run-comp-sweep-btn"
              className="text-[10px] tracking-widest heading border border-volt text-volt px-3 py-2 hover:bg-volt hover:text-white transition-colors disabled:opacity-50">
              {sweepBusy ? "SCANNING…" : "RUN SWEEP NOW"}
            </button>
          </div>
          <p className="text-[11px] text-neutral-500 mt-1">Auto-scans every scraped posting (USA Lacrosse, IWLCA, state assocs) for published salary figures — weekly on Tuesdays, or on demand. Approve to add as a VERIFIED record.</p>
          {sweep.length === 0 ? (
            <div className="mt-3 text-sm text-neutral-500" data-testid="sweep-empty">Queue is clear.</div>
          ) : (
            <div className="mt-3 space-y-2">
              {sweep.map((s) => (
                <div key={s.id} className="border border-neutral-800 p-3 flex flex-wrap items-center gap-3" data-testid={`sweep-item-${s.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">{s.program_name} <span className="text-neutral-500 font-normal text-xs">· {s.division || "?"} · {s.gender} · {s.position}</span></div>
                    <div className="text-xs mt-0.5"><span className="font-bold" style={{ color: "#FF3B30" }}>{fmt(s.salary_min)}{s.salary_max !== s.salary_min ? `–${fmt(s.salary_max)}` : ""}</span>
                      <span className="text-neutral-500"> · {s.source}</span></div>
                    <div className="text-[10px] text-neutral-500 mt-1 truncate">“…{s.snippet}…”</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => handleSweep(s.id, "approve")} data-testid={`sweep-approve-${s.id}`}
                      className="text-[10px] tracking-widest border border-green-600 text-green-400 px-3 py-1.5 hover:bg-green-600 hover:text-white transition-colors">APPROVE → VERIFIED</button>
                    <button onClick={() => handleSweep(s.id, "dismiss")} data-testid={`sweep-dismiss-${s.id}`}
                      className="text-[10px] tracking-widest border border-neutral-600 text-neutral-400 px-3 py-1.5 hover:border-blaze hover:text-blaze transition-colors">DISMISS</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Coverage tracker */}
        <div className="mt-8 border border-neutral-800 bg-appcard p-5" data-testid="comp-coverage-panel">
          <div className="text-xs tracking-widest text-volt">COVERAGE TRACKER · WHERE VERIFIED DATA IS STILL MISSING</div>
          <div className="mt-3 grid md:grid-cols-2 gap-x-8 gap-y-1">
            {["Men", "Women"].map((g) => (
              <div key={g}>
                <div className="text-[10px] tracking-widest text-neutral-500 border-b border-neutral-800 pb-1.5">{g.toUpperCase()}'S</div>
                {coverage.filter((c) => c.gender === g).map((c) => {
                  const hasVerified = c.verified > 0;
                  const total = c.verified + c.publicly_reported + c.estimate + c.coach_submitted;
                  return (
                    <div key={`${c.division}-${c.position}`} className="flex items-center gap-2 py-1 border-b border-neutral-900 text-xs">
                      <span className={`w-2 h-2 shrink-0 ${hasVerified ? "bg-green-500" : c.publicly_reported > 0 ? "bg-yellow-500" : ""}`}
                        style={!hasVerified && c.publicly_reported === 0 ? { backgroundColor: "#FF3B30" } : {}} />
                      <span className="w-8 text-neutral-400">{c.division}</span>
                      <span className="flex-1 text-neutral-300 truncate">{c.position}</span>
                      <span className={hasVerified ? "text-green-400" : "text-neutral-600"}>{c.verified}V</span>
                      <span className={c.publicly_reported ? "text-yellow-500" : "text-neutral-600"}>{c.publicly_reported}R</span>
                      <span className="text-neutral-500">{c.estimate}E</span>
                      <span className="text-neutral-600 w-14 text-right">{total} total</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-neutral-500 mt-3"><span style={{ color: "#FF3B30" }}>■</span> no verified or reported data — hunt here · <span className="text-yellow-500">■</span> reported only · <span className="text-green-400">■</span> verified coverage. V = verified, R = reported, E = estimates.</p>
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap gap-2 items-center" data-testid="comp-filters">
          <input className="input-comp" placeholder="Search program…" value={filters.q} data-testid="comp-search"
            onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          {[["division", DIVISIONS], ["gender", GENDERS], ["position", POSITIONS], ["confidence", CONFIDENCE.map((c) => c.v)]].map(([k, opts]) => (
            <select key={k} className="input-comp" value={filters[k]} data-testid={`comp-filter-${k}`}
              onChange={(e) => setFilters({ ...filters, [k]: e.target.value })}>
              <option value="">{k.toUpperCase()}: ALL</option>
              {opts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
        </div>

        {/* Records table */}
        <div className="mt-4 border border-neutral-800 bg-appcard overflow-x-auto" data-testid="comp-records-table">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] tracking-widest text-neutral-500 border-b border-neutral-800">
              <th className="text-left py-2.5 px-3">PROGRAM</th><th className="text-left pr-3">DIV</th>
              <th className="text-left pr-3">GENDER</th><th className="text-left pr-3">POSITION</th>
              <th className="text-left pr-3">COMPENSATION</th>
              <th className="text-left pr-3 cursor-pointer select-none" data-testid="sort-index-header" onClick={() => setSortByIndex(!sortByIndex)}>
                INDEX {sortByIndex ? "▼" : "↕"}
              </th>
              <th className="text-left pr-3">CONFIDENCE</th>
              <th className="text-left pr-3">SOURCE</th><th className="text-left pr-3">NOTES</th><th></th>
            </tr></thead>
            <tbody>
              {!records ? (
                <tr><td colSpan={10} className="py-6 px-3 text-neutral-500">Loading…</td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan={10} className="py-6 px-3 text-neutral-500" data-testid="comp-empty">No records match.</td></tr>
              ) : shownRecords.map((r) => (
                <tr key={r.id} className="border-b border-neutral-900 hover:bg-[#141414] cursor-pointer" data-testid={`comp-row-${r.id}`}
                  onClick={() => setForm({ ...EMPTY, ...r, salary_min: r.salary_min ?? "", salary_max: r.salary_max ?? "" })}>
                  <td className="py-2.5 px-3 font-semibold">{r.program_name}</td>
                  <td className="pr-3">{r.division}</td>
                  <td className="pr-3 text-neutral-300">{r.gender}</td>
                  <td className="pr-3 text-neutral-300">{r.position}</td>
                  <td className="pr-3 font-semibold whitespace-nowrap">{range(r.salary_min, r.salary_max, r.salary_note)}
                    {r.contract_months ? <span className="text-neutral-500 font-normal text-xs"> · {r.contract_months}mo</span> : ""}
                    {r.fte ? <span className="text-neutral-500 font-normal text-xs"> · {r.fte} FTE</span> : ""}</td>
                  <td className="pr-3">
                    {r.job_id && indices[r.job_id] != null
                      ? <span className="font-bold" style={{ color: indices[r.job_id] >= 70 ? "#FF3B30" : undefined }} data-testid={`comp-index-${r.id}`}>{indices[r.job_id]}</span>
                      : <span className="text-neutral-600">—</span>}
                  </td>
                  <td className="pr-3"><span className={`text-[9px] tracking-widest px-1.5 py-0.5 border ${badge(r.confidence).cls}`}>{badge(r.confidence).label}</span></td>
                  <td className="pr-3 text-neutral-400 text-xs">{r.source || "—"}</td>
                  <td className="pr-3 text-neutral-500 text-xs max-w-[220px] truncate">{r.notes || ""}</td>
                  <td className="pr-3 text-right">
                    <button onClick={(e) => { e.stopPropagation(); del(r.id); }} data-testid={`comp-del-${r.id}`}
                      className="text-neutral-600 hover:text-blaze transition-colors"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-neutral-500 mt-3">Click a row to edit. VERIFIED = employer posting / payroll / contract · REPORTED = credible published info · CC ESTIMATE = modeled from comparables · COACH-SUBMITTED = anonymous, ideally corroborated. Never present an estimate as an individual coach's actual compensation.</p>

        {/* Add / edit modal */}
        {form && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center overflow-y-auto py-10" onClick={() => setForm(null)}>
            <div className="bg-appcard border border-neutral-700 p-6 w-full max-w-2xl" onClick={(e) => e.stopPropagation()} data-testid="comp-form-modal">
              <div className="flex items-center justify-between">
                <div className="heading text-2xl">{form.id ? "EDIT RECORD" : "ADD RECORD"}</div>
                <button onClick={() => setForm(null)} data-testid="comp-form-close"><X size={18} className="text-neutral-400 hover:text-white" /></button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <F label="Program"><input className="input-comp w-full" data-testid="comp-f-program" value={form.program_name} onChange={(e) => setForm({ ...form, program_name: e.target.value })} /></F>
                <F label="Location"><input className="input-comp w-full" value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} /></F>
                <F label="Division"><select className="input-comp w-full" data-testid="comp-f-division" value={form.division} onChange={(e) => setForm({ ...form, division: e.target.value })}>{DIVISIONS.map((d) => <option key={d}>{d}</option>)}</select></F>
                <F label="Gender"><select className="input-comp w-full" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>{GENDERS.map((g) => <option key={g}>{g}</option>)}</select></F>
                <F label="Position"><select className="input-comp w-full" data-testid="comp-f-position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })}>{POSITIONS.map((p) => <option key={p}>{p}</option>)}</select></F>
                <F label="Confidence"><select className="input-comp w-full" data-testid="comp-f-confidence" value={form.confidence} onChange={(e) => setForm({ ...form, confidence: e.target.value })}>{CONFIDENCE.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}</select></F>
                <F label="Salary Min ($)"><input type="number" className="input-comp w-full" data-testid="comp-f-min" value={form.salary_min} onChange={(e) => setForm({ ...form, salary_min: e.target.value })} /></F>
                <F label="Salary Max ($)"><input type="number" className="input-comp w-full" data-testid="comp-f-max" value={form.salary_max} onChange={(e) => setForm({ ...form, salary_max: e.target.value })} /></F>
                <F label="Source"><input className="input-comp w-full" data-testid="comp-f-source" value={form.source || ""} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="IWLCA posting, payroll DB…" /></F>
                <F label="Source URL"><input className="input-comp w-full" value={form.source_url || ""} onChange={(e) => setForm({ ...form, source_url: e.target.value })} /></F>
                <F label="Contract Months"><input type="number" className="input-comp w-full" value={form.contract_months || ""} onChange={(e) => setForm({ ...form, contract_months: e.target.value ? parseInt(e.target.value) : null })} /></F>
                <F label="FTE"><input type="number" step="0.01" className="input-comp w-full" value={form.fte || ""} onChange={(e) => setForm({ ...form, fte: e.target.value ? parseFloat(e.target.value) : null })} /></F>
              </div>
              <F label="Benefits / Structure Notes"><input className="input-comp w-full" value={form.benefits_notes || ""} onChange={(e) => setForm({ ...form, benefits_notes: e.target.value })} placeholder="Housing, tuition, camp income, retirement…" /></F>
              <F label="Notes"><textarea className="input-comp w-full min-h-[60px]" data-testid="comp-f-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F>
              <button onClick={save} disabled={busy || !form.program_name} data-testid="comp-form-save"
                className="mt-4 w-full bg-blaze text-white heading text-lg py-3 hover:glow-blaze transition-all disabled:opacity-50">
                {busy ? "SAVING…" : form.id ? "SAVE CHANGES" : "ADD TO DATABASE"}
              </button>
            </div>
          </div>
        )}
        <FoundingPartnersSection />
      </div>
      <style>{`.input-comp { background: #121212; border: 1px solid #27272A; padding: 8px 10px; color: white; outline: none; font-size: 12px; } .input-comp:focus { border-color: #FF3B30; }`}</style>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block mt-2">
      <div className="text-[10px] tracking-widest text-neutral-500 mb-1">{label.toUpperCase()}</div>
      {children}
    </label>
  );
}
